import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  type RouterHistory,
  redirect,
} from "@tanstack/react-router";
import { AuditLog } from "../features/admin/AuditLog";
import { GoreloAdmin } from "../features/admin/GoreloAdmin";
import { SpacesAdmin } from "../features/admin/SpacesAdmin";
import { ThemeEditor } from "../features/admin/ThemeEditor";
import { ThemesAdmin } from "../features/admin/ThemesAdmin";
import { TokensAdmin } from "../features/admin/TokensAdmin";
import { UsersAdmin } from "../features/admin/UsersAdmin";
import { ClientBinder } from "../features/clients/ClientBinder";
import { ClientDocuments } from "../features/clients/ClientDocuments";
import { ClientItems } from "../features/clients/ClientItems";
import { ClientOverview } from "../features/clients/ClientOverview";
import { ClientSites } from "../features/clients/ClientSites";
import { ClientsPage } from "../features/clients/ClientsPage";
import { ItemPage } from "../features/clients/ItemPage";
import { ClientDiagram, DiagramLoadError } from "../features/diagram/ClientDiagram";
import { DocumentPage } from "../features/document/DocumentPage";
import { NewDocumentPage } from "../features/newdoc/NewDocumentPage";
import { SearchPage } from "../features/search/SearchPage";
import { ApiError } from "../lib/api/client";
import { api } from "../lib/api/endpoints";
import type { Me } from "../lib/api/models";
import { qk } from "../lib/queries";
import { AdminLayout } from "./AdminLayout";
import { AppShell } from "./AppShell";
import { HandbookPage } from "./pages/HandbookPage";
import { NoAccessPage } from "./pages/NoAccessPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { SignInPage } from "./pages/SignInPage";
import { SpacePage } from "./pages/SpacePage";
import { TemplatesPage } from "./pages/TemplatesPage";

type Ctx = { queryClient: QueryClient };
const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : undefined);

const rootRoute = createRootRouteWithContext<Ctx>()({ component: Outlet, notFoundComponent: NotFoundPage });

// Search params are typed optional (`key?:`), so links and navigate() only pass the ones they need.
const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signin",
  validateSearch: (s: Record<string, unknown>): { returnTo?: string } => ({ returnTo: str(s.returnTo) }),
  component: function SignInRoute() {
    return <SignInPage returnTo={signInRoute.useSearch().returnTo} />;
  },
});

const noAccessRoute = createRoute({ getParentRoute: () => rootRoute, path: "/no-access", component: NoAccessPage });

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData({ queryKey: qk.me, queryFn: api.me });
      return { me };
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({ to: "/signin", search: { returnTo: location.href } });
      }
      if (e instanceof ApiError && e.status === 403) throw redirect({ to: "/no-access" });
      throw e;
    }
  },
  component: AppShell,
  notFoundComponent: NotFoundPage,
});

// The start page is the client list (spec §4.4: the product is the MSP's client binders).
const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/clients" });
  },
});

const clientsRoute = createRoute({ getParentRoute: () => appRoute, path: "/clients", component: ClientsPage });

const clientRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clients/$clientId",
  component: function ClientRoute() {
    const { clientId } = clientRoute.useParams();
    return <ClientBinder key={clientId} clientId={clientId} />;
  },
});
const clientOverviewRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/",
  component: function ClientOverviewRoute() {
    return <ClientOverview clientId={clientOverviewRoute.useParams().clientId} />;
  },
});
const clientDocumentsRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/documents",
  component: function ClientDocumentsRoute() {
    return <ClientDocuments clientId={clientDocumentsRoute.useParams().clientId} />;
  },
});
const clientSitesRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/sites",
  component: function ClientSitesRoute() {
    return <ClientSites clientId={clientSitesRoute.useParams().clientId} />;
  },
});
const clientDevicesRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/devices",
  // `site` opens the tab filtered to one site: the diagram's device group links here.
  validateSearch: (s: Record<string, unknown>): { site?: string } => ({ site: str(s.site) }),
  component: function ClientDevicesRoute() {
    const { site } = clientDevicesRoute.useSearch();
    return (
      <ClientItems
        key={`device-${site ?? ""}`}
        clientId={clientDevicesRoute.useParams().clientId}
        category="device"
        site={site}
      />
    );
  },
});
const clientNetworkRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/network",
  component: function ClientNetworkRoute() {
    return <ClientItems key="network" clientId={clientNetworkRoute.useParams().clientId} category="network" />;
  },
});
const clientDiagramRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/diagram",
  errorComponent: DiagramLoadError,
  component: function ClientDiagramRoute() {
    return <ClientDiagram clientId={clientDiagramRoute.useParams().clientId} />;
  },
});
const clientSiteDiagramRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/diagram/$siteId",
  errorComponent: DiagramLoadError,
  component: function ClientSiteDiagramRoute() {
    const { clientId, siteId } = clientSiteDiagramRoute.useParams();
    return <ClientDiagram clientId={clientId} siteId={siteId} />;
  },
});
const clientItemRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: "/items/$itemId",
  component: function ClientItemRoute() {
    const { clientId, itemId } = clientItemRoute.useParams();
    return <ItemPage key={itemId} clientId={clientId} itemId={itemId} />;
  },
});

const handbookRoute = createRoute({ getParentRoute: () => appRoute, path: "/handbook", component: HandbookPage });
const oldTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/templates",
  beforeLoad: () => {
    throw redirect({ to: "/admin/templates", replace: true });
  },
});

const spaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/topics/$slug",
  component: function SpaceRoute() {
    return <SpacePage slug={spaceRoute.useParams().slug} />;
  },
});

const docRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/d/$ref",
  validateSearch: (s: Record<string, unknown>): { edit?: true } =>
    s.edit === true || s.edit === "true" ? { edit: true } : {},
  component: function DocRoute() {
    const { ref } = docRoute.useParams();
    const { edit } = docRoute.useSearch();
    return <DocumentPage key={ref} docRef={ref} startEditing={edit === true} />;
  },
});

const searchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/search",
  validateSearch: (s: Record<string, unknown>): { q: string } => ({ q: str(s.q) ?? "" }),
  component: function SearchRoute() {
    return <SearchPage q={searchRoute.useSearch().q} />;
  },
});

const newRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/new",
  validateSearch: (
    s: Record<string, unknown>,
  ): { space?: string; parent?: string; template?: string; kind?: string } => ({
    space: str(s.space),
    parent: str(s.parent),
    template: str(s.template),
    kind: str(s.kind),
  }),
  component: function NewRoute() {
    return <NewDocumentPage {...newRoute.useSearch()} />;
  },
});

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/admin",
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData<Me>(qk.me);
    if (me?.role !== "admin") throw redirect({ to: "/no-access" });
  },
  component: AdminLayout,
});
const adminIndex = createRoute({
  getParentRoute: () => adminRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/topics" });
  },
});
const adminSpaces = createRoute({ getParentRoute: () => adminRoute, path: "/topics", component: SpacesAdmin });
const adminTemplates = createRoute({ getParentRoute: () => adminRoute, path: "/templates", component: TemplatesPage });
const adminGorelo = createRoute({ getParentRoute: () => adminRoute, path: "/gorelo", component: GoreloAdmin });
const adminThemes = createRoute({ getParentRoute: () => adminRoute, path: "/themes", component: ThemesAdmin });
const adminTheme = createRoute({
  getParentRoute: () => adminRoute,
  path: "/themes/$themeId",
  component: function ThemeRoute() {
    return <ThemeEditor themeId={adminTheme.useParams().themeId} />;
  },
});
const adminUsers = createRoute({ getParentRoute: () => adminRoute, path: "/users", component: UsersAdmin });
const adminTokens = createRoute({ getParentRoute: () => adminRoute, path: "/tokens", component: TokensAdmin });
const adminAudit = createRoute({ getParentRoute: () => adminRoute, path: "/audit", component: AuditLog });

// The addresses from before the Handbook and its topics (document types spec §5.2). They redirect, so bookmarks
// and links in tickets keep working.
const oldInternalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/internal",
  beforeLoad: () => {
    throw redirect({ to: "/handbook", replace: true });
  },
});
const oldSpaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/spaces/$slug",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/topics/$slug", params, replace: true });
  },
});
const oldAdminSpaces = createRoute({
  getParentRoute: () => adminRoute,
  path: "/spaces",
  beforeLoad: () => {
    throw redirect({ to: "/admin/topics", replace: true });
  },
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  noAccessRoute,
  appRoute.addChildren([
    homeRoute,
    clientsRoute,
    clientRoute.addChildren([
      clientOverviewRoute,
      clientDocumentsRoute,
      clientSitesRoute,
      clientDevicesRoute,
      clientNetworkRoute,
      clientDiagramRoute,
      clientSiteDiagramRoute,
      clientItemRoute,
    ]),
    handbookRoute,
    oldInternalRoute,
    oldTemplatesRoute,
    spaceRoute,
    oldSpaceRoute,
    docRoute,
    searchRoute,
    newRoute,
    adminRoute.addChildren([
      adminIndex,
      adminSpaces,
      oldAdminSpaces,
      adminTemplates,
      adminGorelo,
      adminThemes,
      adminTheme,
      adminUsers,
      adminTokens,
      adminAudit,
    ]),
  ]),
]);

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    context: { queryClient },
    history,
    defaultPreload: "intent",
    scrollRestoration: false,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
